import type { Node } from '@xyflow/react';

import { DomainMetadataTargetType } from '../../api/api';
import { getContextArtifactId, parseContextArtifactId } from './ContextArtifactSelection';
import type { DomainNodeData } from './DomainGraphUtils';

export const ENUM_NODE_ID_PREFIX = 'enum-';
export const ENTITY_ARTIFACT_ID_PREFIX = 'entity-';
export const FILE_ARTIFACT_ID_PREFIX = 'file-';

export function getEnumNodeId(enumId: string) {
	return `${ENUM_NODE_ID_PREFIX}${enumId}`;
}

export function getEnumIdFromNodeId(nodeId: string) {
	return nodeId.startsWith(ENUM_NODE_ID_PREFIX) ? nodeId.substring(ENUM_NODE_ID_PREFIX.length) : nodeId;
}

export function getEntityArtifactId(entityId: string) {
	return getContextArtifactId({ kind: 'entity', id: entityId });
}

export function getEnumArtifactId(enumId: string) {
	return getContextArtifactId({ kind: 'enum', id: enumId });
}

export function getFileArtifactId(fileId: string) {
	return getContextArtifactId({ kind: 'domain-file', id: fileId });
}

export function isEntityArtifactId(artifactId: string | null) {
	return artifactId?.startsWith(ENTITY_ARTIFACT_ID_PREFIX) ?? false;
}

export function isEnumArtifactId(artifactId: string | null) {
	return artifactId?.startsWith(ENUM_NODE_ID_PREFIX) ?? false;
}

export function isFileArtifactId(artifactId: string | null) {
	return artifactId?.startsWith(FILE_ARTIFACT_ID_PREFIX) ?? false;
}

export function getEntityIdFromArtifactId(artifactId: string) {
	return parseContextArtifactId(artifactId)?.id ?? artifactId;
}

export function getEnumIdFromArtifactId(artifactId: string) {
	return parseContextArtifactId(artifactId)?.id ?? artifactId;
}

export function getFileIdFromArtifactId(artifactId: string) {
	return parseContextArtifactId(artifactId)?.id ?? artifactId;
}

export function getSelectedNodeId(selectedEntityId?: string, selectedEnumId?: string) {
	return selectedEntityId ?? (selectedEnumId ? getEnumNodeId(selectedEnumId) : undefined);
}

export function getDomainMetadataIdentity(node: Node<DomainNodeData>) {
	if (node.data.kind === 'entity') {
		return {
			id: node.id,
			targetType: DomainMetadataTargetType.Entity,
			key: `${DomainMetadataTargetType.Entity}:${node.id}`,
		};
	}

	const enumId = node.data.enumModel.id;
	return {
		id: enumId,
		targetType: DomainMetadataTargetType.Enum,
		key: `${DomainMetadataTargetType.Enum}:${enumId}`,
	};
}
